import Vue from 'vue';
import api from '../../api/editRide';
import attractionApi from '../../api/attraction';
import sharedPlaceImagesApi from '../../api/sharedPlaceImages';
import { pickToRecord, shouldPrefillSharedImage, isPrefilledImage } from '../../utils/sharedPlaceImages';
import { router } from '../../main';
import { pollJobStatus } from '../../common/pollJobStatus';

// Reports a saved Pixabay pick so an admin can approve it for every ride.
// A failure here must never block the save itself.
async function recordPixabayPick( state, apptoken ) {
  const record = pickToRecord({
    pixabayPick: state.newAttractionPixabayPick,
    placeId: state.newAttractionPlaceId,
    savedImageUrl: state.newAttractionImage,
  });
  if ( !record ) return;
  try {
    await sharedPlaceImagesApi.recordPick( apptoken, record );
  } catch ( e ) {
    console.error( 'failed to record shared place image', e ); // eslint-disable-line no-console
  }
}

const state = {
  newAttractionTitle: '',
  newAttractionAddress: '',
  newAttractionText: '',
  newAttractionLink: '',
  newAttractionOpeningHours: null,
  newAttractionReview: null,
  newAttractionRate: null,
  newAttractionLat: null,
  newAttractionLng: null,
  newAttractionImage: null,
  /** { pixabayId, uploadedUrl } when the current image came from a Pixabay pick on a known place */
  newAttractionPixabayPick: null,
  /** URL of the image we pre-filled from the shared pool; null once a person changes the image */
  newAttractionPrefilledImage: null,
  newAttractionPreviewImage: null,
  newAttractionPlannedDate: null,
  newAttractionPlannedDateIndex: null,
  newAttractionId: null,
  newAttractionMode: null,
  newAttractionPlaceId: null,
  newAttractionGoogleImages: [],
  newAttractionGoogleImage: null,
  newAttractionErrores: [],
  newAttractionCategory: null,
  newAttractionSubcategory: null,
  newAttractionUploadValue: 0,
  newAttractionVideoUrl: null,
  newAttractionAudioUrl: null,
  newAttractionVideoUploadValue: 0,
  newAttractionAudioUploadValue: 0,
  newAttractionVoucherUrls: [],
  newAttractionVoucherUploadValues: {},
  /** Master attraction row set when editing base attraction from master-compare flow */
  referenceMasterAttraction: null,
};

const getters = {
  newAttractionTitle: state => state.newAttractionTitle,
  newAttractionAddress: state => state.newAttractionAddress,
  newAttractionText: state => state.newAttractionText,
  newAttractionLink: state => state.newAttractionLink,
  newAttractionOpeningHours: state => state.newAttractionOpeningHours,
  newAttractionReview: state => state.newAttractionReview,
  isNewAttractionReview: state => state.newAttractionReview!=null,
  newAttractionRate: state => state.newAttractionRate,
  isNewAttractionRate: state => state.newAttractionRate!=null,
  newAttractionLat: state => state.newAttractionLat,
  newAttractionLng: state => state.newAttractionLng,
  newAttractionImage: state => state.newAttractionImage,
  isNewAttractionImage: state => state.newAttractionImage!=null,
  newAttractionPreviewImage: state => state.newAttractionPreviewImage,
  newAttractionPlannedDate: state => state.newAttractionPlannedDate,
  newAttractionPlannedDateIndex: state => state.newAttractionPlannedDateIndex,
  newAttractionId: state => state.newAttractionId,
  newAttractionMode: state => state.newAttractionMode,
  isNewAttractionEditMode: state => state.newAttractionMode=='edit',
  newAttractionPlaceId: state => state.newAttractionPlaceId,
  newAttractionCategory: state => state.newAttractionCategory,
  newAttractionSubcategory: state => state.newAttractionSubcategory,
  newAttractionGoogleImages: state => state.newAttractionGoogleImages,
  newAttractionGoogleImage: state => state.newAttractionGoogleImage,
  newAttractionErrores: state => state.newAttractionErrores,
  newAttractionUploadValue: state => state.newAttractionUploadValue,
  newAttractionVideoUrl: state => state.newAttractionVideoUrl,
  newAttractionAudioUrl: state => state.newAttractionAudioUrl,
  isNewAttractionVideoUrl: state => state.newAttractionVideoUrl != null,
  isNewAttractionAudioUrl: state => state.newAttractionAudioUrl != null,
  newAttractionVideoUploadValue: state => state.newAttractionVideoUploadValue,
  newAttractionAudioUploadValue: state => state.newAttractionAudioUploadValue,
  newAttractionVoucherUrls: state => state.newAttractionVoucherUrls,
  newAttractionVoucherUploadValues: state => state.newAttractionVoucherUploadValues,
  referenceMasterAttraction: state => state.referenceMasterAttraction,
  };

const actions = {
  newAttractionLoadDefault({commit}){
    commit( 'setNewAttractionPlannedDateIndex', null );
    commit( 'setNewAttractionPlannedDate', null );
    commit( 'setNewAttractionId', null );
    commit( 'setNewAttractionTitle', '' );
    commit( 'setNewAttractionAddress', '' );
    commit( 'setNewAttractionText', '' );
    commit( 'setNewAttractionLink', '' );
    commit( 'setNewAttractionOpeningHours', null );
    commit( 'setNewAttractionReview', null );
    commit( 'setNewAttractionRate', null ); 
    commit( 'setNewAttractionLat', null );
    commit( 'setNewAttractionLng', null );
    commit( 'setNewAttractionImage', null );
    commit( 'setNewAttractionPixabayPick', null );
    commit( 'setNewAttractionPreviewImage', null );
    commit( 'setNewAttractionMode', null );
    commit( 'setNewAttractionPlaceId', null );
    commit( 'setNewAttractionGoogleImages', [] );
    commit( 'setNewAttractionGoogleImage', null );
    commit( 'setNewAttractionErrores', [] );
    commit( 'setNewAttractionCategory', null );
    commit( 'setNewAttractionUploadValue', 0 );
    commit( 'setNewAttractionVideoUrl', null );
    commit( 'setNewAttractionAudioUrl', null );
    commit( 'setNewAttractionVideoUploadValue', 0 );
    commit( 'setNewAttractionAudioUploadValue', 0 );
    commit( 'setNewAttractionVoucherUrls', [] );
    commit( 'setReferenceMasterAttraction', null );
  },

  async uploadAttractionIfEditMode({ dispatch,  getters } ){
    const mode = getters.newAttractionMode;
    if( mode != 'edit'){ return; }
    dispatch( 'uploadAttractionFromServer' );
  },

  async creatcontentByAi({ getters ,rootState, commit, dispatch} ){
    
    dispatch( 'showModal', "loadingIcon" );
    const rideId = rootState.ride?.rideId || null;
    const { apptoken } = rootState.appauth;
    const attractionId = getters.newAttractionId;
    const surentTextAtTheBox = state.newAttractionText;
    if ( surentTextAtTheBox != '' )
    {
      dispatch( 'showModalWithParameters',
        { errorNumber : "atraction_text_box_is_not_empty",
          type:'textConverter' 
        } );
        return;
    }
    var attraction = {}
    attraction['title'] = state.newAttractionTitle;
    attraction['address'] = state.newAttractionAddress;
    attraction['lat'] = state.newAttractionLat;
    attraction['lng'] = state.newAttractionLng;
    attraction['rideId'] = rideId;
    const rideLanguage = rootState.ride?.rideHeader?.ride?.language ||
                         rootState.ride?.ride?.ride?.language;
    if (rideLanguage) {
      const normalizedRideLanguage = rideLanguage.toLowerCase();
      attraction['language'] = normalizedRideLanguage
    }
    
    const response = await attractionApi.getPlaceAiInformation(apptoken, attractionId, attraction);
    const jobId = response.data.job_id;
    if (!jobId) {
      dispatch('showModal', '');
      return;
    }

    const { status, result } = await pollJobStatus(() => attractionApi.getJobStatus(apptoken, jobId));
    dispatch('showModal', '');
    if (status === 'complete') {
      const parsedData = typeof result === 'string' ? JSON.parse(result) : result;
      if (parsedData && parsedData.success === 'true') {
        commit('setNewAttractionText', parsedData.text);
      }
    }
  },

  async uploadAttractionFromServer( { getters, commit, rootState,  dispatch },
                                    args = {  'attractionId': 0,
                                              'sorce':'internal' } ){
    var attractionId = args.attractionId;
    if( attractionId == 0 ){
      attractionId = getters.newAttractionId;
      args.sorce = 'internal'
    }
    const { apptoken } = rootState.appauth;
    var response = await attractionApi.getAttraction( apptoken, attractionId, args.sorce );
    const parsedData = response.data;
    if(parsedData.error){
      dispatch( 'showModalWithParameters',
        { errorNumber : parsedData.error,
          type:'textConverter' 
        }
      );
      // this is a work arownd becose the popup is run over by this atraction popup
      commit('setNewAttractionTitle', parsedData.error );
      return
    }

    commit('setNewAttractionTitle', parsedData.title );
    commit('setNewAttractionAddress', parsedData.address );
    commit('setNewAttractionText', parsedData.text );
    commit('setNewAttractionLink', parsedData.link );
    commit('setNewAttractionRate', parsedData.rate );
    commit('setNewAttractionLat', parsedData.lat );
    commit('setNewAttractionLng', parsedData.lng );
    commit('setNewAttractionImage', parsedData.imageUrl );
    commit('setNewAttractionPlaceId', parsedData.placeId || parsedData.place_id );
    commit('setNewAttractionImage', parsedData.imageUrl );
    // Load video and audio URLs if available (support both camelCase and snake_case from API)
    const videoUrl = parsedData.videoUrl || parsedData.video_url;
    if (videoUrl) {
      commit('setNewAttractionVideoUrl', videoUrl);
    }
    if (parsedData.audioUrl) {
      commit('setNewAttractionAudioUrl', parsedData.audioUrl);
    }
    // Load voucher URLs if available
    commit('setNewAttractionVoucherUrls', parsedData.voucherUrls || []);
    if( parsedData.openingHours ){
      commit('setNewAttractionOpeningHours', parsedData.openingHours.weekday_text );
    }
    // Load category data if available
    if (parsedData.category_id) {
      commit('setNewAttractionCategory', parsedData.category_id);
      if (parsedData.subcategory_id) {
        commit('setNewAttractionSubcategory', parsedData.subcategory_id);
      }
    }

    if( parsedData.review ){
      commit('setNewAttractionReview', parsedData.review );
    }
  },

  async validateAttractionInput( { state, commit }, attraction ){
    const errors = [];
    commit('setNewAttractionErrores', []);

    if (attraction['title'] == '') {
      errors.push('attractionValidationTitleRequired');
    }
    
    if (attraction['lat'] == null) {
      errors.push('attractionValidationLocationRequired');
    }

    if (attraction['title'] && attraction['title'].length > 140) {
      errors.push('attractionValidationTitleTooLong');
    }

    // Check if image is uploading or waiting for download URL
    if ( state.newAttractionUploadValue > 0 && state.newAttractionUploadValue < 100 ) {
      errors.push('attractionValidationImageUploading');
    } else if ( state.newAttractionUploadValue === 100 ) {
      // Upload is complete but we're waiting for the download URL
      // If image is still null, we haven't received the new URL yet
      if ( state.newAttractionImage == null ) {
        errors.push('attractionValidationImageUrlPending');
      }
    }

    // Check if video is uploading or waiting for download URL
    if ( state.newAttractionVideoUploadValue > 0 && state.newAttractionVideoUploadValue < 100 ) {
      errors.push('attractionValidationVideoUploading');
    } else if ( state.newAttractionVideoUploadValue === 100 ) {
      // Upload is complete but we're waiting for the download URL
      // If video URL is still null, we haven't received the new URL yet
      if ( state.newAttractionVideoUrl == null && attraction['videoUrl'] == null ) {
        errors.push('attractionValidationVideoUrlPending');
      }
    }

    // Check if audio is uploading or waiting for download URL
    if ( state.newAttractionAudioUploadValue > 0 && state.newAttractionAudioUploadValue < 100 ) {
      errors.push('attractionValidationAudioUploading');
    } else if ( state.newAttractionAudioUploadValue === 100 ) {
      // Upload is complete but we're waiting for the download URL
      // If audio URL is still null, we haven't received the new URL yet
      if ( state.newAttractionAudioUrl == null && attraction['audioUrl'] == null ) {
        errors.push('attractionValidationAudioUrlPending');
      }
    }

    // Check if any voucher is uploading
    const uploadValues = Object.values(state.newAttractionVoucherUploadValues);
    if (uploadValues.some(v => v > 0 && v < 100)) {
      errors.push('attractionValidationVoucherUploading');
    }

    commit('setNewAttractionErrores', errors);

    if ( errors.length > 0 )
      return false
    return true;
  },

  async updateAttraction({ state, rootState, getters, dispatch }, options = {} ){
    const rideId = rootState.ride?.rideId || null;
    var attraction = {}
    attraction['title'] = state.newAttractionTitle;
    attraction['address'] = state.newAttractionAddress;
    attraction['text'] = state.newAttractionText;
    attraction['link'] = state.newAttractionLink;
    attraction['rate'] = state.newAttractionRate;
    attraction['lat'] = state.newAttractionLat;
    attraction['lng'] = state.newAttractionLng;
    attraction['imageUrl'] = state.newAttractionImage;
    attraction['placeId'] = state.newAttractionPlaceId;
    attraction['videoUrl'] = state.newAttractionVideoUrl;
    attraction['audioUrl'] = state.newAttractionAudioUrl;
    attraction['voucherUrls'] = state.newAttractionVoucherUrls;

    // Include category data if a category is selected
    if (state.newAttractionCategory) {
      attraction['categoryId'] = state.newAttractionCategory;
      if (state.newAttractionSubcategory) {
        attraction['subcategoryId'] = state.newAttractionSubcategory;
      }
    }

    if ( ! await dispatch('validateAttractionInput', attraction ) )
      return;
    const { apptoken } = rootState.appauth;
    const attractionId = getters.newAttractionId;
    dispatch('showModal', 'loadingIcon');
    const response = await attractionApi.updateAttraction( apptoken,
                                          attractionId,
                                          attraction);
    const data = response.data;

    await dispatch('showModal', '');
    if (data.ServerMassageCode !== 'success') {
      dispatch('showModalWithParameters', { errorNumber: data.ServerMassageCode, type: 'textConverter' });
      return;
    }

    // The ride attraction is saved with this image now; record before any early
    // return below (master compare modal) so the pick is never lost.
    await recordPixabayPick( state, apptoken );

    if (data.shouldAskMasterUpdate && data.masterAttraction && data.attractionNew) {
      dispatch('showModalWithParameters', {
        inerModalParameters: {
          masterAttraction: data.masterAttraction,
          attractionNew: data.attractionNew,
          rideId,
          attractionId,
        },
        type: 'masterAttractionCompare',
      });
      return;
    }

    // Planning attributes are saved here, before newAttractionLoadDefault clears
    // newAttractionId and router.push tears the page down. The server resolves the
    // pool entry itself, so passing this attraction's id is enough.
    if ( options.enrichment ) {
      const { apptoken } = rootState.appauth;
      const enrichmentAttractionId = getters.newAttractionId;
      if ( enrichmentAttractionId ) {
        try {
          await attractionApi.saveAttractionEnrichment( apptoken,
                                                        enrichmentAttractionId,
                                                        options.enrichment );
        } catch ( e ) {
          // The attraction itself is saved; losing the enrichment must not block it.
          console.error( 'failed to save attraction enrichment', e ); // eslint-disable-line no-console
        }
      }
    }

    await dispatch('newAttractionLoadDefault');
    // this is an work arownd that id the call arive from editing the atraction from the ride- we have the ride id and return to the ride
    // if not we arived from the ridem manager edt attractio - this is very ugly and we should fix it as soon as posible
    if( rideId ){
      router.push('/single_ride_with_map/' + rideId);
    }
    else{
      router.push('/ride_manager_attraction' );
    }
  },

  completeAttractionUpdateAfterMasterCompare({ dispatch }, { rideId }) {
    dispatch('newAttractionLoadDefault');
    if (rideId) {
      router.push('/single_ride_with_map/' + rideId);
    } else {
      router.push('/ride_manager_attraction');
    }
  },

  async directUpdateMasterAttractionFromCompare({ dispatch, rootState }, { rideId, attractionNew, masterAttraction }) {
    const { apptoken } = rootState.appauth;
    const masterId = masterAttraction && masterAttraction.id != null
      ? masterAttraction.id
      : attractionNew.id;
    const attraction = {
      title: attractionNew.title || '',
      address: attractionNew.address || '',
      text: attractionNew.text || '',
      link: attractionNew.link || '',
      lat: attractionNew.lat || null,
      lng: attractionNew.lng || null,
      imageUrl: attractionNew.image || null,
      placeId: attractionNew.place_id || null,
      videoUrl: attractionNew.videoUrl || null,
      audioUrl: attractionNew.audioUrl || null,
      voucherUrls: attractionNew.voucherUrls || [],
    };
    dispatch('showModal', 'loadingIcon');
    const response = await attractionApi.updateAttraction(apptoken, masterId, attraction);
    const data = response.data;
    await dispatch('showModal', '');
    if (data.ServerMassageCode !== 'success') {
      dispatch('showModalWithParameters', { errorNumber: data.ServerMassageCode, type: 'textConverter' });
      return;
    }
    dispatch('completeAttractionUpdateAfterMasterCompare', { rideId });
  },

  /**
   * Master compare modal: open editor with ride row fields but master attraction id for the save URL.
   * This lets the manager update the base/master attraction using the data they just saved on the ride.
   */
  openAttractionFromMasterCompare({ commit, dispatch }, { attractionNew, masterAttraction }) {
    if (!attractionNew) {
      return;
    }
    const mergedId = masterAttraction && masterAttraction.id != null
      ? masterAttraction.id
      : attractionNew.id;
    dispatch('newAttractionLoadDefault');
    commit('setNewAttractionId', mergedId);
    commit('setNewAttractionMode', 'edit');
    if (masterAttraction) {
      commit('setReferenceMasterAttraction', masterAttraction);
    }
    commit('setNewAttractionTitle', attractionNew.title || '');
    commit('setNewAttractionText', attractionNew.text || '');
    commit('setNewAttractionAddress', attractionNew.address || '');
    commit('setNewAttractionLink', attractionNew.link || '');
    commit('setNewAttractionLat', attractionNew.lat || null);
    commit('setNewAttractionLng', attractionNew.lng || null);
    commit('setNewAttractionImage', attractionNew.image || null);
    commit('setNewAttractionPlaceId', attractionNew.place_id || null);
    commit('setNewAttractionVideoUrl', attractionNew.videoUrl || null);
    commit('setNewAttractionAudioUrl', attractionNew.audioUrl || null);
    commit('setNewAttractionVoucherUrls', attractionNew.voucherUrls || []);
    if (router.currentRoute.path === '/add_new_attraction_to_ride') {
      window.scrollTo(0, 0);
    } else {
      router.push('/add_new_attraction_to_ride').catch(() => {});
    }
  },
  async createAttraction({ state, rootState, getters, dispatch, commit }, options = {} ){
    var attraction = {}
    const rideId = rootState.ride.rideId;
    const plannedDate = getters.newAttractionPlannedDate;
    attraction['plannedDate'] = plannedDate;
    attraction['title'] = state.newAttractionTitle;
    attraction['address'] = state.newAttractionAddress;
    attraction['text'] = state.newAttractionText;
    attraction['link'] = state.newAttractionLink;
    attraction['rate'] = state.newAttractionRate;
    attraction['lat'] = state.newAttractionLat;
    attraction['lng'] = state.newAttractionLng;
    attraction['googleImage'] = state.newAttractionGoogleImage;
    attraction['imageUrl'] = state.newAttractionImage;
    attraction['placeId'] = state.newAttractionPlaceId;
    attraction['videoUrl'] = state.newAttractionVideoUrl;
    attraction['audioUrl'] = state.newAttractionAudioUrl;
    attraction['voucherUrls'] = state.newAttractionVoucherUrls;

    // Only include category data if a category is selected
    if (state.newAttractionCategory) {
      attraction['categoryId'] = state.newAttractionCategory;
      if (state.newAttractionSubcategory) {
        attraction['subcategoryId'] = state.newAttractionSubcategory;
      }
    }

    if ( ! await dispatch('validateAttractionInput', attraction ) )
      return;
    const { apptoken } = rootState.appauth;
    dispatch( 'showModalWithParameters',
    { errorNumber : "updating_the_ride",
      type:'textConverter' } );
    var response = await api.createAttraction(  apptoken,
                                                rideId,
                                                attraction);
    dispatch( 'showModal', '' );
    if( response.data.ServerMassageCode != "success" ){
      dispatch( 'showModalWithParameters',
      { errorNumber : response.data.ServerMassageCode,
        type:'textConverter' 
      } );
      return
    }
    // Planning attributes belong on the manager's POOL entry, not on this ride's
    // copy -- the pool is the only place the AI ride planner reads. The server
    // returns both ids. This must happen before newAttractionLoadDefault clears the
    // state and router.push tears the page down.
    const createdAttractionId = response.data.attractionId;
    if ( createdAttractionId ) {
      commit( 'setNewAttractionId', createdAttractionId );
    }
    const poolAttractionId = response.data.poolAttractionId;
    if ( poolAttractionId && options.enrichment ) {
      try {
        await attractionApi.saveAttractionEnrichment( apptoken,
                                                      poolAttractionId,
                                                      options.enrichment );
      } catch ( e ) {
        // The attraction itself is saved; losing the enrichment must not block it.
        console.error( 'failed to save attraction enrichment', e ); // eslint-disable-line no-console
      }
    }

    await recordPixabayPick( state, apptoken );

    await dispatch( 'newAttractionLoadDefault' );
    if ( rootState.ride.onboarding ) {
      const ride = rootState.ride.ride || {};
      const attractionsByDay = ride.attractionsByDay || [];
      const totalAttractionsBefore = attractionsByDay.reduce(
        (sum, day) => sum + (day.daily_attractions || []).length,
        0
      );
      const isFirstAttraction = totalAttractionsBefore === 0;
      if ( isFirstAttraction ) {
        router.push('/after-first-attraction/' + rideId);
        return
      }
    } 
    router.push('/single_ride_with_map/' + rideId);
  },
  goToNewAttraction({ rootState, commit, dispatch }, dayIndex ) {
    dispatch( 'newAttractionLoadDefault' );
    const ride = rootState.ride;
    const { apptoken } = rootState.appauth;
    if( apptoken ){
      const rawDate = ride.ride.attractionsByDay[dayIndex].dateTime;
      const dateStr = new Date(rawDate).toISOString().split("T")[0]; // Get "2025-08-02"
      const [year, month, day] = dateStr.split("-").map(Number);
      const palannedDate = new Date(year, month - 1, day, 23, 59); // Local time without shifting
      commit('setNewAttractionPlannedDateIndex', dayIndex );
      commit('setNewAttractionPlannedDate', palannedDate.toLocaleString("en-US"));
      router.push('/add_new_attraction_to_ride');
    }
    else{
      dispatch( 'openLoginForm' );
    }
  },
  editAttraction({ commit, dispatch }, attractionId ){
    dispatch('newAttractionLoadDefault' );
    commit( 'setNewAttractionId', attractionId )
    commit( 'setNewAttractionMode', 'edit' )
    dispatch('uploadAttractionFromServer' );
    router.push('/add_new_attraction_to_ride');
  },

  cleanHistoryAndEditAttraction({ dispatch }, attractionId ){
    dispatch( 'cleanAllRideInformation' )
    dispatch('editAttraction', attractionId );
  },

  async lowedAttractionAndShow({ dispatch }, args = {  'attractionId': 0,
                                                               'sorce':'internal' } )
  {
    dispatch( "showModal", "loadingIcon" );
    await dispatch( "lowedAttraction", args );
    dispatch( "showModal", "attraction" );
                                                    
  },
  async lowedAttraction({ commit, dispatch }, args = {  'attractionId': 0,
                                                        'sorce':'internal' } ){
    dispatch('newAttractionLoadDefault' );
    commit( 'setNewAttractionId', args.attractionId )
    // commit( 'setNewAttractionMode', 'edit' )
    await dispatch('uploadAttractionFromServer', args );
  },
  viewAttraction({ dispatch }, args = {  'attractionId': 0,
                                                        'sorce':'internal' } ){
    dispatch('lowedAttraction', args );
    router.push('/attractionInformation');
  },

  async uplowadGoogleImages({ state }, place ){
    var { newAttractionGoogleImages } = state;
    newAttractionGoogleImages.length = 0
    for( var i = 0; i < 6; i++ ){
      newAttractionGoogleImages.push( place.photos[i].getUrl({maxWidth: 500, maxHeight: 500}) )
    }
  },

  // Shows the admin-approved image of the chosen place on a new attraction.
  // Does not set the Pixabay pick: an approved image is already shared.
  async prefillSharedPlaceImage({ state, rootState, commit, getters }, placeId) {
    // An image we pre-filled for the previous place is not a person's choice:
    // treat it as empty so this place's approved image can take its spot.
    if (!getters.isNewAttractionEditMode && isPrefilledImage({
      currentImage: state.newAttractionImage,
      prefilledImage: state.newAttractionPrefilledImage,
    })) {
      commit('setNewAttractionImage', null);
    }
    if (!shouldPrefillSharedImage({
      isEditMode: getters.isNewAttractionEditMode,
      currentImage: state.newAttractionImage,
      placeId,
    })) return;
    try {
      const { apptoken } = rootState.appauth;
      const response = await sharedPlaceImagesApi.getApprovedImage(apptoken, placeId);
      const imageUrl = response.data && response.data.imageUrl;
      // The user may have picked an image, or another place, meanwhile.
      if (imageUrl && !state.newAttractionImage && state.newAttractionPlaceId === placeId
          && !getters.isNewAttractionEditMode) {
        commit('setNewAttractionImage', imageUrl);
        commit('setNewAttractionPrefilledImage', imageUrl);
      }
    } catch (error) {
      console.error('prefillSharedPlaceImage failed', error);
    }
  },

  async checkIfUserHasPlaceIdInAttractions({ state, rootState, getters, dispatch }, googlePlaceId ){
    const rideId = rootState.ride.rideId;
    const { apptoken } = rootState.appauth;
    var placeInfo = {};
    placeInfo['googlePlaceId'] = googlePlaceId;
    var response = await api.checkIfUserHasPlaceIdInAttractions(  apptoken,
                                                                  rideId,
                                                                  placeInfo);
    // response.data.userAttractionCheckResult = {
    //     "has_attraction_with_place_id": False,
    //     "is_assigned_to_this_ride": False,
    //     "lat": None,
    //     "lng": None
    // }
    const parsedData = response.data;
    if( parsedData.userAttractionCheckResult.has_attraction_with_place_id &&
        !parsedData.userAttractionCheckResult.is_assigned_to_this_ride)
      {
        dispatch( 'showModalWithParameters', { 
          inerModalParameters: {
            lat: state.newAttractionLat,
            lng: state.newAttractionLng,
            dayIndex: getters.newAttractionPlannedDateIndex,
            plannedDate: state.newAttractionPlannedDate
          },
          type: 'existingAttractionOptions'
        });
      }
    
    if( response.data.ServerMassageCode != "success" ){
      dispatch( 'showModalWithParameters',
      { errorNumber : response.data.ServerMassageCode,
        type:'textConverter' 
      } );
      return
    }
  },
};

const mutations = {
  setNewAttractionTitle: (state, newAttractionTitle) => {
    state.newAttractionTitle = newAttractionTitle;
  },
  setNewAttractionAddress: (state, newAttractionAddress) => {
    state.newAttractionAddress = newAttractionAddress;
  },
  setNewAttractionText: (state, newAttractionText) => {
    state.newAttractionText = newAttractionText;
  },
  setNewAttractionLink: (state, newAttractionLink) => {
    state.newAttractionLink = newAttractionLink;
  },
  setNewAttractionOpeningHours: (state, openingHours) => {
    state.newAttractionOpeningHours = openingHours;
  },
  setNewAttractionReview: (state, review) => {
    state.newAttractionReview = review;
  },
  setNewAttractionRate: (state, newAttractionRate) => {
    state.newAttractionRate = newAttractionRate;
  },
  setNewAttractionLat: (state, newAttractionLat) => {
    state.newAttractionLat = newAttractionLat;
  },
  setNewAttractionLng: (state, newAttractionLng) => {
    state.newAttractionLng = newAttractionLng;
  },
  setNewAttractionPixabayPick: (state, pick) => {
    state.newAttractionPixabayPick = pick;
  },
  setNewAttractionPrefilledImage: (state, url) => {
    state.newAttractionPrefilledImage = url;
  },
  setNewAttractionImage: (state, newAttractionImage) => {
    state.newAttractionImage = newAttractionImage;
    // Any change of image (upload, Pixabay, remove, reset) ends the pre-fill;
    // the prefill action sets it again right after its own commit.
    state.newAttractionPrefilledImage = null;
    // Reset upload value when image is set
    if (newAttractionImage != null) {
      state.newAttractionUploadValue = 0;
    }
  },
  setNewAttractionPreviewImage: (state, newAttractionPreviewImage) => {
    state.newAttractionPreviewImage = newAttractionPreviewImage;
  },
  setNewAttractionPlannedDate: (state, plannedDate) => {
    state.newAttractionPlannedDate = plannedDate;
  },
  setNewAttractionPlannedDateIndex: (state, plannedDateIndex) => {
    state.newAttractionPlannedDateIndex = plannedDateIndex;
  },
  setNewAttractionId: (state, attractionId) => {
    state.newAttractionId = attractionId;
  },
  setNewAttractionMode: (state, mode) => {
    state.newAttractionMode = mode;
  },
  setNewAttractionPlaceId: (state, PlaceId) => {
    state.newAttractionPlaceId = PlaceId;
  },
  setNewAttractionCategory: (state, category) => {
    state.newAttractionCategory = category;
    // Reset subcategory when category changes
    state.newAttractionSubcategory = null;
  },
  setNewAttractionSubcategory: (state, subcategory) => {
    state.newAttractionSubcategory = subcategory;
  },
  setNewAttractionGoogleImages: (state, googleImages) => {
    state.newAttractionGoogleImages = googleImages;
  },
  setNewAttractionGoogleImage: (state, googleImage) => {
    state.newAttractionGoogleImage = googleImage;
  },
  setNewAttractionErrores: ( state, errores) => {
    state.newAttractionErrores = errores;
  },
  setNewAttractionUploadValue: (state, uploadValue) => {
    state.newAttractionUploadValue = uploadValue;
  },
  setNewAttractionVideoUrl: (state, videoUrl) => {
    state.newAttractionVideoUrl = videoUrl;
    // Reset upload value when video is set
    if (videoUrl != null) {
      state.newAttractionVideoUploadValue = 0;
    }
  },
  setNewAttractionAudioUrl: (state, audioUrl) => {
    state.newAttractionAudioUrl = audioUrl;
    // Reset upload value when audio is set
    if (audioUrl != null) {
      state.newAttractionAudioUploadValue = 0;
    }
  },
  setNewAttractionVideoUploadValue: (state, uploadValue) => {
    state.newAttractionVideoUploadValue = uploadValue;
  },
  setNewAttractionAudioUploadValue: (state, uploadValue) => {
    state.newAttractionAudioUploadValue = uploadValue;
  },
  addVoucherUrl: (state, url) => {
    if (state.newAttractionVoucherUrls.length < 10) {
      state.newAttractionVoucherUrls.push(url);
    }
  },
  removeVoucherUrl: (state, index) => {
    state.newAttractionVoucherUrls.splice(index, 1);
  },
  setVoucherUploadValue: (state, { index, value }) => {
    Vue.set(state.newAttractionVoucherUploadValues, index, value);
  },
  clearVoucherUploadValue: (state, index) => {
    Vue.delete(state.newAttractionVoucherUploadValues, index);
  },
  setNewAttractionVoucherUrls: (state, urls) => {
    state.newAttractionVoucherUrls = Array.isArray(urls) ? urls : [];
  },
  setReferenceMasterAttraction: (state, reference) => {
    state.referenceMasterAttraction = reference;
  },
};

export default {
  state,
  getters,
  actions,
  mutations
};
